import { Engine, EngineResult, RuleProperties } from "json-rules-engine";

/**
 * Interface for any object that has specifications with key/value pairs.
 * This allows getItemRules to work with CatalogicalItem, ItemDetails, IItemVersion, etc.
 */
interface HasSpecifications {
  specifications?: { key?: string; value?: any }[];
  taxonomyId?: string;
}

/**
 * Extracts rules from an item's specifications.
 * Works with any object that has a specifications array (CatalogicalItem, ItemDetails, IItemVersion, etc.)
 * @param item - The item to extract rules from (must have a specifications array)
 * @returns Array of RuleProperties parsed from the item's specifications
 */
export const getItemRules = (item: HasSpecifications): RuleProperties[] => {
  try {
    return JSON.parse(item.specifications?.find((spec) => spec.key === "rules")?.value || "[]") as RuleProperties[];
  } catch (error) {
    console.error("Failed to parse item rules:", item.taxonomyId, error);
    return [];
  }
};

// https://github.com/CacheControl/json-rules-engine
export class RulesEngineFactory {
  public static getRulesEngine(rules: RuleProperties[]): Engine {
    const engine = new Engine(rules);

    engine.addOperator("all-in-array", (factValue, jsonValue) => {
      if (!Array.isArray(factValue)) {
        return false;
      }
      return factValue.every((val: string) => val === jsonValue);
    });

    engine.addOperator("equal-case-insensitive", (factValue: any, jsonValue: any) => {
      if (factValue == null || jsonValue == null) {
        return false;
      }
      return String(factValue).toLowerCase() === String(jsonValue).toLowerCase();
    });

    engine.addOperator("notEqual-case-insensitive", (factValue: any, jsonValue: any) => {
      if (factValue == null || jsonValue == null) {
        return true;
      }
      return String(factValue).toLowerCase() !== String(jsonValue).toLowerCase();
    });

    const notNullOrUndefined = (factValue: any, jsonValue: any) => factValue !== null && factValue !== undefined;

    engine.addOperator("not-null-or-undefined", notNullOrUndefined);

    engine.addOperator(
      "has-value-trimmed",
      (factValue: any, jsonValue: any) => typeof factValue === "string" && factValue.trim() !== jsonValue
    );

    engine.addOperator("containsObject", (factValue: any, jsonValue: any) => {
      if (!Array.isArray(factValue) || !Array.isArray(jsonValue)) return false;
      return factValue.some((item: any) => jsonValue.includes(item));
    });

    return engine;
  }

  public static logRulesResult(engineResult: EngineResult) {
    console.info(
      `Rules engine result:\n\tSuccess: [${engineResult.results.map(
        (rule) => rule.name
      )}]\n\tFailure: [${engineResult.failureResults.map((rule) => rule.name)}]`
    );
  }
}
